import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useUserAndGameContext } from "../../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../../context/SuperPickemContext";
import LoadingScreen from "../../../LoadingScreen/LoadingScreen";
import UserSubmitBar from "../../../GamePageComponents/SubmitBars/UserSubmitBar";
import isEqual from "lodash/isEqual";
import GameNotReadyModal from "../../../GameModals/GameNotReadyModal/GameNotReadyModal";
import useNavigateToTop from "../../../../hooks/general/useNavigateToTop";
import { useDispatch } from "react-redux";
import { userLogoutAction } from "../../../../redux/actions/loginImport";
import { useMediaQuery } from "@mui/material";
import QuickPickGamesComing from "../../../QuickPickGames/CardPieces/QuickPickGamesComing";
import SuperPickemBracketSubmissionModal from "../../../GameModals/BracketGameModals/SuperPickemBracketSubmissionalModal";
import AgnosticSixteenTeamMobileBracket from "../../../Brackets/SixteenTeamBracket/AgnosticSixteenTeamMobileBracket";
import TemplatedCustomNavBar from "../../../NavigationBars/BracketNavigationBar/TemplatedCustomNavBar";
import { useSaveSuperPickemBracketPicks } from "../../../../hooks/pools/useSaveSuperPickemBracket";
import { DateTime } from "luxon";
import CountdownClock from "../../../GameHomePage/GameHomePageComponents/CountdownClock";


const SixteenTeamMobileBracket = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isMobile = useMediaQuery("(max-width: 1350px)");
    const navigateToTop = useNavigateToTop();
    const dispatch = useDispatch();

    const {
        tournamentSchedule,
        teams,
        usingSeriesLengths,
        picksInfo,
        lastRound,
        hasBracketDeadlinePassed,
        selectedUserBracketInfo,
    } = useSuperPickemContext();

    const {
        myUserId,
        masterPoolId,
        pickIndex,
        masterPoolData,
        gameAddress,
        gameId,
        league,
        pickState,
        originalPicks,
        setPickState,
        bracketState,
        setBracketState,
        originalBracketPicks,
        seriesLengths,
        setSeriesLengths,
        originalSeriesLengths,
        gameData,
        setUnsavedPicks,
        isUserInThisPool,
        revealTime,
        firstGameTime,
        page,
    } = useUserAndGameContext();
    //console.log("Series Lengths:", seriesLengths)
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [submitBarOpen, setSubmitBarOpen] = useState(false);
    const [preBracketModalOpen, setPreBracketModalOpen] = useState(false);
    const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
    const [submissionData, setSubmissionData] = useState(null);

    const { mutate: saveSuperPickemBracketPicks } = useSaveSuperPickemBracketPicks();


    const timeNowInNewYork = DateTime.now().setZone("America/New_York");
    const revealTimeInNewYork = DateTime.fromISO(revealTime, { zone: "America/New_York" });
    const firstGameTimeInNewYork = DateTime.fromISO(firstGameTime, { zone: "America/New_York" });

    const revealTimePassed = timeNowInNewYork > revealTimeInNewYork;
    const firstGameTimePassed = timeNowInNewYork > firstGameTimeInNewYork;

    const countdownInfo = gameData?.countdownInfo || masterPoolData?.countdownInfo;
    const countdownText = (revealTime && !revealTimePassed) ? countdownInfo?.revealText : (firstGameTime && !firstGameTimePassed) ? (isUserInThisPool ? countdownInfo?.countdownTextInGame : countdownInfo?.countdownTextNotInGame) : countdownInfo?.intervalCountdown ? countdownInfo?.intervalText : null;
    const countdownTime = (revealTime && !revealTimePassed) ? revealTimeInNewYork : (firstGameTime && !firstGameTimePassed) ? firstGameTimeInNewYork : null;

    const myBracketModalInfo = masterPoolData?.modalInfo?.myBracket;
    //console.log("My Bracket Modal Info: ", myBracketModalInfo);

    const effectiveBracketState = useMemo(() => {
        return page === 'my-bracket' 
            ? bracketState 
            : (selectedUserBracketInfo?.bracketPicks || []);
    }, [page, bracketState, selectedUserBracketInfo?.bracketPicks]);
        
    const effectiveSeriesLengths = useMemo(() => {
        return page === 'my-bracket' 
            ? seriesLengths 
            : (selectedUserBracketInfo?.seriesLengths || []);
    }, [page, seriesLengths, selectedUserBracketInfo?.seriesLengths]);

    // Gather points by Round (Combined with Series Lengths)
    function memoizePointsByRound(bracketState, seriesLengths) {
        const roundPointsMap = {};
        
        // Process bracketState points
        bracketState.forEach(item => {
            const round = item.round;
            if (!roundPointsMap[round]) {
                roundPointsMap[round] = 0;
            }
            // Only add points if they exist
            if (item.points !== undefined) {
                roundPointsMap[round] += Number(item.points); // Ensure numeric addition
            }
        });
        
        // Process seriesLengths points
        seriesLengths.forEach(item => {
            const round = item.round;
            if (!roundPointsMap[round]) {
                roundPointsMap[round] = 0;
            }
            // Only add points if they exist
            if (item.points !== undefined) {
                roundPointsMap[round] += Number(item.points); // Ensure numeric addition
            }
        });
        
        // Convert to required format
        const result = Object.entries(roundPointsMap).map(([round, points]) => ({
            round: parseInt(round),
            points: points
        }));
        
        // Sort by round
        result.sort((a, b) => a.round - b.round);
        
        // console.log("Points calculated:", JSON.stringify(result));
        return result;
    }

    const pointsByRound = useMemo(() => {
        
        return memoizePointsByRound(effectiveBracketState, effectiveSeriesLengths);
    }, [effectiveBracketState, effectiveSeriesLengths]);
    //console.log("Points by Round: ", pointsByRound);


      
    // Initialize roundDash from URL or default to 1
    const [roundDash, setRoundDash] = useState(() => {
        const navParam = searchParams.get('bracketNav');
        return navParam ? parseInt(navParam, 10) : 1;
    });

    const scrollToRound = (roundRef) => {        
        if (!roundRef?.current) {
            return;
        }
        
        const gameElement = roundRef.current.querySelector('.agnostic-regional-bracket-game');
        if (!gameElement) {
            return;
        }
        
        // Get element position
        const rect = gameElement.getBoundingClientRect();
        
        // Vertical scroll
        window.scrollTo({
            top: rect.top + window.pageYOffset - 150,
            behavior: 'smooth'
        });
        
        // Also handle horizontal scroll based on which round
        const container = document.querySelector('.agnostic-regional-bracket-container');
        if (!container) {
            return;
        }
        
        // Find the column element that contains the game
        const columnElement = roundRef.current.closest('.agnostic-regional-bracket-column');
        
        if (!columnElement) {
            // If we can't find the column, reset to beginning
            container.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
            return;
        }
        
        // Determine column position
        const columnRect = columnElement.getBoundingClientRect();
        
        // Calculate scroll position to center the column
        const containerRect = container.getBoundingClientRect();
        const centerOffset = (containerRect.width / 2) - (columnRect.width / 2);
        let targetScrollLeft = columnRect.left - containerRect.left - centerOffset + container.scrollLeft;
        
        // Special handling for round2Ref - add 30px to the scroll position
        if (roundRef === round2Ref) {
            targetScrollLeft += 60;
        }
        
        // Scroll horizontally to center the column
        container.scrollTo({
            left: Math.max(0, targetScrollLeft),
            behavior: 'smooth'
        });
    };
    
    // Special function for championship round
    const scrollToChampionship = () => {
        // Get the championship container and bracket container
        const champElement = document.querySelector('.agnostic-regional-bracket-column-champion');
        const container = document.querySelector('.agnostic-regional-bracket-container');
        
        if (champElement && container) {
            // First get the position of the championship element
            const rect = champElement.getBoundingClientRect();
            
            // Vertical scroll
            window.scrollTo({
                top: rect.top + window.pageYOffset + 250,
                behavior: 'smooth'
            });
            
            // Scroll all the way to the right for the championship column
            const scrollWidth = container.scrollWidth;
            container.scrollTo({
                left: scrollWidth - container.clientWidth,
                behavior: 'smooth'
            });
        }
    };
   

    // Use Effect to see if originalPicks is different from pickState
    // And if originalTiebreaker is different from tiebreaker
    // If so setSubmitBarOpen to true, otherwise false
    useEffect(() => {
        if (originalBracketPicks && bracketState) {
            const picksAreEqual = 
                originalBracketPicks?.length === bracketState?.length &&
                originalBracketPicks?.every(originalPick =>
                    bracketState?.some(currentPick => isEqual(originalPick, currentPick))
                );
                
            let seriesLengthsAreEqual = true;
            if (usingSeriesLengths && seriesLengths && originalSeriesLengths) {
                seriesLengthsAreEqual = 
                    originalSeriesLengths?.length === seriesLengths?.length &&
                    originalSeriesLengths?.every(originalLength =>
                        seriesLengths?.some(currentLength => isEqual(originalLength, currentLength))
                    );
            }
            
            // Set submit bar to open if either picks or tiebreakers are different
            setSubmitBarOpen(!picksAreEqual || !seriesLengthsAreEqual);
            setUnsavedPicks(!picksAreEqual || !seriesLengthsAreEqual);
        }
    }, [originalBracketPicks, bracketState, originalSeriesLengths, seriesLengths, usingSeriesLengths, setUnsavedPicks]);


    // Add refs and state for sticky navigation
    const observerRef = useRef(null);
    const containerRef = useRef(null);
    const [isStuck, setIsStuck] = useState(false);

    const round1Ref = useRef(null);
    const round2Ref = useRef(null);
    const round3Ref = useRef(null);
    const round4Ref = useRef(null);
    const round5Ref = useRef(null);

     // Handle scroll for sticky navigation
     useEffect(() => {
        const handleScroll = () => {
            if (observerRef.current && containerRef.current) {
                const observerPosition = observerRef.current.getBoundingClientRect();
                setIsStuck(observerPosition.top <= 80);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Update URL when roundDash changes while preserving other params
    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('bracketNav', roundDash.toString());
        setSearchParams(newParams);
    }, [roundDash, setSearchParams, searchParams]);

    const setRoundDashPosition = useMemo(() => {
        return {
            1: () => {
                if (round1Ref.current) {
                    scrollToRound(round1Ref);
                }
            },
            2: () => {
                if (round2Ref.current) {
                    scrollToRound(round2Ref);
                }
            },
            3: () => {
                if (round3Ref.current) {
                    scrollToRound(round3Ref);
                }
            },
            4: () => {
                if (round4Ref.current) {
                    scrollToRound(round4Ref);
                }
            },
            5: () => {
                if (round5Ref.current) {
                    scrollToChampionship();
                }
            }
        };
    }, [round1Ref, round2Ref, round3Ref, round4Ref, round5Ref]);

     // Update roundDash and bracket based on URL when component mounts
     useEffect(() => {
        const navParam = searchParams.get('bracketNav');
        if (navParam) {
            const roundNumber = parseInt(navParam, 10);
            setRoundDash(roundNumber);
            setRoundDashPosition[roundNumber]?.();
        } else {
            setRoundDash(1);
            const newParams = new URLSearchParams(searchParams);
            newParams.set('bracketNav', '1');
            setSearchParams(newParams);
            setRoundDashPosition[1]?.();
        }
    }, [searchParams, setSearchParams, setRoundDashPosition]);

    const tournamentGames = tournamentSchedule?.Games;

    const picksToMake = gameData?.gameParameters?.picksToMake ?? masterPoolData?.gameParameters?.picksToMake;
    const tiebreakerCount = gameData?.gameParameters?.tiebreakerCount ?? masterPoolData?.gameParameters?.tiebreakerCount;

    const checkForCompleteRoundAndScroll = (round, latestSeriesLengths = seriesLengths) => {
        // Get the maximum number of picks required for this round
        const maxPicksForRound = picksInfo?.find(pick => pick.round === round)?.picks || 0;
        
        // Get all bracket picks for this round
        const roundBracketPicks = bracketState.filter(pick => pick.round === round);
        
        // Count only picks that have both bracket selection AND series length
        let completePicks = 0;
        
        // For each bracket pick in this round
        roundBracketPicks.forEach(bracketPick => {
          // Check if there's a matching series length
          const hasSeriesLength = latestSeriesLengths?.some(seriesLength => 
            seriesLength.matchupId === bracketPick.matchupId && 
            seriesLength.round === bracketPick.round
          );
          
          if (hasSeriesLength) {
            completePicks++;
          }
        });
        
        // Only scroll if we've made exactly the right number of complete picks
        if (completePicks === maxPicksForRound) {
          
          // Find the next interval in the sequence
          const currentIndex = picksInfo?.findIndex(r => r.round === round);
          const nextInterval = picksInfo[currentIndex + 1];
          
          if (!nextInterval) {
            setTimeout(() => {
              scrollToChampionship();
            }, 300);
            return;
          }
          
          if (nextInterval) {
            const nextRound = nextInterval.round;
            
            // Map from round values to round refs
            const roundToRef = {
              1: round1Ref,
              2: round2Ref,
              3: round3Ref,
              4: round4Ref,
              5: round5Ref
            };
            
            // Get the ref for the next interval based on its round value
            const nextRoundRef = roundToRef[nextRound];
            
            if (nextRoundRef?.current) {
              setTimeout(() => {
                scrollToRound(nextRoundRef);
              }, 300);
            }
          }
        }
      };

    const handleGameClick = (teamId, matchupId, round, seedNumber) => {
        //console.log("Clicked game: ", teamId, matchupId, round, seedNumber);

        let newBracketPicks = [...bracketState];

        const existingPickIndex = newBracketPicks.findIndex(pick =>
            pick.matchupId === matchupId && pick.round === round
        );

        if (existingPickIndex !== -1 && newBracketPicks[existingPickIndex].teamId === teamId) {
            return; // No change if the same team is clicked again
        }

        const newPick = {
            teamId,
            round,
            matchupId,
            teamSeed: seedNumber
        };

        let finalPicks;
        if (existingPickIndex !== -1) {
            // Get the team that's being replaced
            const teamBeingReplaced = newBracketPicks[existingPickIndex].teamId;

            // First remove the existing pick from that position (to avoid duplicates)
            newBracketPicks.splice(existingPickIndex, 1);

            // Then filter out any other instances of the team being replaced in later rounds
            finalPicks = newBracketPicks.filter(pick =>
                pick.round < round || pick.teamId !== teamBeingReplaced
            );

            // Now add the new pick
            finalPicks.push(newPick);

            // Sort to maintain order
            finalPicks.sort((a, b) => {
                if (a.round === b.round) {
                    return a.teamSeed - b.teamSeed;
                }
                return a.round - b.round;
            });

            setBracketState(finalPicks);
        } else {
            finalPicks = [...newBracketPicks, newPick];

            const getMacksPicksForRound = (roundNum) => {
                return picksInfo?.find(pick => pick.round === roundNum)?.picks;
            };

            const isChampionshipRound = round === lastRound;

            const previousRoundPicks = bracketState?.filter(pick =>
                pick.round === round
            )?.length;

            const nextRoundPicks = finalPicks?.filter(pick =>
                pick.round === round
            )?.length;

            const maxPicksForRound = getMacksPicksForRound(round);
            //console.log("!! Max Picks for Round: ", maxPicksForRound);

            // Update the state with the new picks
            setBracketState(finalPicks);

            // Check if we've just completed a round and should scroll to the next round
            if (
                (isChampionshipRound && nextRoundPicks === maxPicksForRound) || 
                (!isChampionshipRound && nextRoundPicks === maxPicksForRound && previousRoundPicks < maxPicksForRound)
            ) {
                if (usingSeriesLengths) {
                    // Count picks that have both bracket pick and series length
                    const completePicks = finalPicks.filter(bracketPick => 
                        bracketPick.round === round && 
                        seriesLengths?.some(seriesLength => 
                            seriesLength.matchupId === bracketPick.matchupId && 
                            seriesLength.round === bracketPick.round
                        )
                    ).length;
                    
                    // Only proceed with auto-scroll if all picks are truly complete
                    if (completePicks < maxPicksForRound) {
                        return;
                    }
                }
                 // Find the current interval information
            const currentInterval = picksInfo?.find(r => r.round === round);
            
            if (currentInterval) {
                // Find the next interval in the sequence
                const currentIndex = picksInfo?.findIndex(r => r.round === round);
                const nextInterval = picksInfo[currentIndex + 1];

    
                // Special handling for the last interval - scroll to championship
                if (!nextInterval) {
                    setTimeout(() => {
                        scrollToChampionship();
                    }, 300);
                    return;
                }
                
                if (nextInterval) {
                    // Instead of using roundDash which doesn't exist,
                    // use the round property directly
                    const nextRound = nextInterval.round;
                    
                    // Map from round values to round refs
                    const roundToRef = {
                        1: round1Ref,
                        2: round2Ref,
                        3: round3Ref,
                        4: round4Ref,
                        5: round5Ref
                    };
                    
                    // Get the ref for the next interval based on its round value
                    const nextRoundRef = roundToRef[nextRound];
                    
                    if (nextRoundRef?.current) {
                        setTimeout(() => {
                            scrollToRound(nextRoundRef);
                        }, 300);
                    }
                }
                
            }
        }
    };


    };


    const handleBracketSubmit = async () => {
        if (hasSubmitted) return;
        setHasSubmitted(true);
        const pickData = {
            userId: myUserId,
            masterPoolId: masterPoolId,
            index: Number(pickIndex),
            bracketPicks: bracketState,
            seriesLengths: seriesLengths,
        }

        try {
            await saveSuperPickemBracketPicks(pickData, {
                onSuccess: (response) => {
                    //console.log("Picks submitted successfully: ", response);
                    setSubmissionData(response);
                    setSubmissionModalOpen(true);
                },
                onError: (error) => {
                    console.error("Error submitting picks: ", error);
                    if (error.response.status === 401) {
                        dispatch(userLogoutAction());
                    } else {
                        setSubmissionData({ error: "An error occurred while submitting your picks." });
                        setSubmissionModalOpen(true);
                    }
                }
            });
        } catch (error) {
            console.error("Error submitting picks: ", error);
            setSubmissionData({ error: "An error occurred while submitting your picks." });
            setSubmissionModalOpen(true);
        }
    }


    const handleCancel = () => {
        setBracketState(originalBracketPicks);
        setSeriesLengths(originalSeriesLengths);
    };

    const handleClearBracket = () => {
        setBracketState([]);
        setSeriesLengths([]);
    }

    const showCancelButton = useMemo(() => {
        return (
            (originalBracketPicks && originalBracketPicks.length > 0) ||
            (usingSeriesLengths && originalSeriesLengths && originalSeriesLengths.length > 0)
        );
    }, [originalBracketPicks, usingSeriesLengths, originalSeriesLengths]);

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    useEffect(() => {
        if (!isUserInThisPool) {
            navigateToTop(`/${uriEncoded(gameAddress)}?mId=${masterPoolId}&gId=${gameId}&page=home`);
        }
    }, [isUserInThisPool, gameAddress, masterPoolId, gameId, navigateToTop]);

    return (
        <>
            {countdownText && (
                <CountdownClock
                    text={countdownText}
                    targetTimeISO={countdownTime}
                    countdownTextColor={countdownInfo?.textColor}
                    countdownBackgroundColor={countdownInfo?.backgroundColor}
                />
            )}
            {myBracketModalInfo && (
                <>
                    <QuickPickGamesComing
                        page="my-bracket"
                        title={myBracketModalInfo.title}
                        body={myBracketModalInfo.body}
                        quickPick={false}
                    />
                    
                    <Box sx={{ marginBottom: '48px' }} />
                    
                </>
            )}

                
            {!myBracketModalInfo && (
                <>
                    {isMobile && (
                        <>
                            <div style={{ position: 'relative', zIndex: 10 }}>
                                <div
                                    ref={observerRef}
                                    style={{
                                        height: '1px',
                                        position: 'relative',
                                    }}
                                />
                                <div
                                    ref={containerRef}
                                    style={{
                                        height: '25px',
                                        backgroundColor: '#fff',
                                        width: '100%',
                                        position: isStuck ? 'fixed' : 'relative',
                                        top: isStuck ? '80px' : '0',
                                        marginBottom: '16px',
                                        zIndex: 10,
                                    }}
                                >
                                    <TemplatedCustomNavBar
                                        roundDash={roundDash}
                                        setRoundDash={setRoundDash}
                                        setRoundDashPosition={setRoundDashPosition}
                                        bracketState={bracketState}
                                        seriesLengths={seriesLengths}
                                        usingSeriesLengths={usingSeriesLengths}
                                        hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                        picksInfo={picksInfo}
                                    />
                                </div>
                            </div>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '32px',
                                        fontWeight: '700',
                                        marginTop: '32px',
                                    }}
                                >
                                    {page === 'my-bracket' ? 'My Bracket' : `${selectedUserBracketInfo?.username}'s Bracket`}
                                </Typography>
                            </Box>

                            <AgnosticSixteenTeamMobileBracket
                                games={tournamentGames}
                                teams={teams}
                                league={league}
                                handleGameClick={handleGameClick}
                                round1Ref={round1Ref}
                                round2Ref={round2Ref}
                                round3Ref={round3Ref}
                                round4Ref={round4Ref}
                                round5Ref={round5Ref}
                                bracketState={page === 'my-bracket' ? bracketState : selectedUserBracketInfo?.bracketPicks || []}
                                seriesLengths={page === 'my-bracket' ? seriesLengths : selectedUserBracketInfo?.seriesLengths || []}
                                setSeriesLengths={setSeriesLengths}
                                lastRound={lastRound}
                                picksInfo={picksInfo}
                                hasBracketDeadlinePassed={hasBracketDeadlinePassed}
                                checkForCompleteRoundAndScroll={checkForCompleteRoundAndScroll}
                                tiebreakerCount={tiebreakerCount}
                                pointsByRound={pointsByRound}
                            />
                      </>
                    )}
                        
                    <Box sx={{ position: 'relative' }}>
                        {submitBarOpen && (
                            <UserSubmitBar 
                                picksToMake={picksToMake}
                                tiebreakerCount={tiebreakerCount}
                                handleSubmit={handleBracketSubmit}
                                handleCancel={handleCancel}
                                hasSubmitted={hasSubmitted}
                                handleClear={handleClearBracket}
                                showCancelButton={showCancelButton}
                                usingBracketState={true}
                                usingSeriesLengths={usingSeriesLengths}
                            />
                        )}

                        {submissionModalOpen && (
                            <SuperPickemBracketSubmissionModal
                                modalOpen={submissionModalOpen}
                                setModalOpen={setSubmissionModalOpen}
                                submissionData={submissionData}
                            />
                        )}
                    </Box>
                </>
            )}
        </>
    );
};

export default SixteenTeamMobileBracket;